import React, { useEffect, useState } from "react";
import { Notification, toast } from "@telia/teddy";
import _get from "lodash/get";
import { useDispatch, useSelector } from "react-redux";
import { CUSTOMER_TYPE, FORM_ID } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import { ChangeSimCardRequestBody } from "../../../../../../models/actionPayloads/addMember";
import { ExtraSimType } from "../../../../../../models/common";
import {
    addExtraSimToCart,
    removeItemByIdFromCart,
    setActiveSubscription,
} from "../../../../checkout/state/actions/cartActions";
import { getActiveSubscription, isSubscriptionDraft } from "../../../../checkout/state/selectors/cartSelectors";
import { getSelectedPhoneNumber } from "../../../../checkout/state/selectors/phoneNumberSelectors";
import {
    getSubscriptions,
    isServiceSubscription as isServiceSubscriptionSelector,
    isSubscriptionBroadband,
    isSubscriptionPrepaid as isSubscriptionPrepaidSelector,
} from "../../../../checkout/state/selectors/subscriptionsSelectors";
import { setValue, resetCustomerInfo } from "../../../state/actions";
import { post as Ajax_post } from "../../../../app/Ajax";
import { API_URL_RETAILER_CHANGE_SIM_CARD } from "../../../api";
import {
    chooseCustomerPhoneNumber,
    customerSwitchPhoneNumberPanel,
    setCustomerDetails,
} from "../../../state/actions/customerActions";
import { RETAILER_SET_CUSTOMER_DETAILS } from "../../../state/actionTypes";
import { resetCurrentPhoneNumberCheckMessages } from "../../../state/actions/phoneNumberActions";
import {
    setReopenExistingSubscriptionPhone,
    clearExistingSubscriptionForChange,
    nextSubscriptionStep,
    setExistingSubscriptionForChange,
    setCompletedSubscriptionSteps,
} from "../../../state/actions/subscriptionStepsActions";
import * as CUSTOMER_INFO_SELECTORS from "../../../state/selectors/customerInfoSelectors";
import { getExistingSubscriptionForChange } from "../../../state/selectors/customerSelectors";
import * as LOADING_SELECTORS from "../../../../loading/state/selectors";
import {
    getFormById,
    showAddCustomerInputFields as showAddCustomerInputFieldsSelector,
} from "../../../state/selectors/formSelectors";
import {
    getBreakageFeeAmount,
    getCurrentPhoneNumberCheckMessages,
    getSelectedPanel,
} from "../../../state/selectors/phoneNumbersSelectors";
import ChooseSimCard from "../../../views/Dashboard/ChooseSimCard";
import { getSimNumberError } from "../../../views/Dashboard/simCardUtils";
import type { SimDeliveryType, SimType } from "../../../views/Dashboard/simCardUtils";
import { CollapsibleStep } from "../../molecules/CollapsibleStep";
import { ChooseSimType } from "../../molecules/ChooseSimType";
import ExistingSubscriptionDiscardModal from "../../molecules/ExistingSubscriptionDiscardModal/ExistingSubscriptionDiscardModal";
import { PortingDateCard } from "../../molecules/PortingDateCard";
import { SubscriptionStepsControls } from "../../molecules/SubscriptionStepsControls";
import { SimChoice, SimConfig } from "../../molecules/RetailerSubscriptionSelectable";
import ChoosePhoneNumber from "../ChoosePhoneNumber/ChoosePhoneNumber";
import { ChangeScope } from "../../../../../../models/state";

interface Props {
    subscriptionId: string;
    existingSubscriptionEditScope?: ChangeScope;
}

type StepId = "phoneNumber" | "simCard" | "portinDate";

const stepOrder: Record<StepId, number> = {
    phoneNumber: 1,
    simCard: 2,
    portinDate: 3,
};

const PhoneNumber = ({ subscriptionId, existingSubscriptionEditScope }: Props) => {
    const dispatch = useDispatch();
    const existingSubscriptionForChange = useSelector((state: RootState) => getExistingSubscriptionForChange(state));
    const isExistingSubscription = !!existingSubscriptionForChange;
    const isSimScopeChange = existingSubscriptionEditScope === "sim";
    const changeableAdditionalSimcards = (existingSubscriptionForChange?.additionalSimcards ?? []).filter(
        (sim) => sim.simType === "DATASIM" || sim.simType === "TVILLINGSIM",
    );
    const hasChangeableExtraSims = changeableAdditionalSimcards.length > 0;
    const [hasChosenVoiceSimType, setHasChosenVoiceSimType] = useState(false);
    const [isSubmittingExtraSimChange, setIsSubmittingExtraSimChange] = useState(false);
    const isLoading = useSelector((state: RootState) => LOADING_SELECTORS.isLoading(state));
    const [selectedStep, setSelectedStep] = useState<StepId>(isExistingSubscription ? "simCard" : "phoneNumber");
    const selectedPanel = useSelector((state: RootState) => getSelectedPanel(state));
    const isEsimSelected = useSelector((state: RootState) => CUSTOMER_INFO_SELECTORS.isEsimSelected(state));
    const isKeepCurrentSim = useSelector((state: RootState) => CUSTOMER_INFO_SELECTORS.isKeepCurrentSim(state));
    const isDraftSubscription = useSelector((state: RootState) => isSubscriptionDraft(state, subscriptionId));
    const isPhysicalSimSelected = useSelector((state: RootState) =>
        CUSTOMER_INFO_SELECTORS.isPhysicalSimSelected(state),
    );
    const isSimFromStoreSelected = useSelector((state: RootState) =>
        CUSTOMER_INFO_SELECTORS.isSimFromStoreSelected(state),
    );
    const [showPortinStep, setShowPortinStep] = useState(
        selectedPanel === CUSTOMER_TYPE.PORT_IN && !isExistingSubscription,
    );
    const [selectedSim, setSelectedSim] = useState<SimType>(() => {
        if (existingSubscriptionForChange?.bundleId === subscriptionId || isKeepCurrentSim) {
            return "keep_current";
        }

        if (isPhysicalSimSelected) {
            return "sim";
        }

        if (isEsimSelected) {
            return "esim";
        }

        return selectedPanel === CUSTOMER_TYPE.PORT_IN ? "sim" : "esim";
    });
    const [selectedSimDelivery, setSelectedSimDelivery] = useState<SimDeliveryType>(() =>
        isPhysicalSimSelected && !isSimFromStoreSelected ? "sent_home" : "from_store",
    );
    const [simNumber, setSimNumber] = useState("");
    const [showSimErrors, setShowSimErrors] = useState(false);
    const [isSimNumberInUse, setIsSimNumberInUse] = useState(false);
    const [discardModalSubscriptionId, setDiscardModalSubscriptionId] = useState<string | null>(null);
    // eSIM type and EID state - applies to all subscription types
    const [esimType, setEsimType] = useState<"esim-qr" | "esim-push">("esim-qr");
    const [eid, setEid] = useState("");
    const [isSubmittingBroadbandUser, setIsSubmittingBroadbandUser] = useState(false);

    const currentPhoneNumberCheckMessages = useSelector((state: RootState) =>
        getCurrentPhoneNumberCheckMessages(state, subscriptionId),
    );
    const breakageFeeAmount = useSelector((state: RootState) => getBreakageFeeAmount(state, subscriptionId));
    const userForm = useSelector((state: RootState) => getFormById(state, FORM_ID.CUSTOMER_PRODUCT_USER));
    const activeSubscription = useSelector((state: RootState) => getActiveSubscription(state));
    const existingSubscriptionPhoneNumber = useSelector((state: RootState) =>
        getSelectedPhoneNumber(state, subscriptionId),
    );

    const showAddCustomerInputFields = useSelector((state: RootState) => showAddCustomerInputFieldsSelector(state));
    const isServiceSubscription = useSelector((state: RootState) =>
        isServiceSubscriptionSelector(state, activeSubscription),
    );
    const isBroadbandSubscription = useSelector((state: RootState) => isSubscriptionBroadband(state, subscriptionId));
    const isPrepaidSubscription = useSelector((state: RootState) =>
        isSubscriptionPrepaidSelector(state, subscriptionId),
    );
    const subscriptions = useSelector((state: RootState) => getSubscriptions(state));
    const existingStoreSimNumber = _get(subscriptions, `${subscriptionId}.storeSimNumber`, "") as string;
    const hasPrefilledSimSelection =
        existingSubscriptionForChange?.bundleId === subscriptionId ||
        isKeepCurrentSim ||
        isPhysicalSimSelected ||
        isEsimSelected;

    const showKeepCurrent = isExistingSubscription;
    const normalizedSimNumber = simNumber.replace(/\D/g, "");
    const hasDuplicateSimNumber =
        selectedSim === "sim" &&
        selectedSimDelivery === "from_store" &&
        normalizedSimNumber.length > 0 &&
        Object.entries(subscriptions).some(([id, subscription]) => {
            if (id === subscriptionId) {
                return false;
            }

            if (!subscription?.isSimFromStoreSelected) {
                return false;
            }

            return _get(subscription, "storeSimNumber", "").replace(/\D/g, "") === normalizedSimNumber;
        });
    const duplicateSimNumberError = hasDuplicateSimNumber
        ? "SIM-nummeret er allerede brukt for et annet abonnement i handlekurven"
        : undefined;

    useEffect(() => {
        // Skip phone number step if existing or if changing an added sub
        if (isExistingSubscription || !isDraftSubscription) {
            setSelectedStep("simCard");
        }
    }, [isExistingSubscription, isDraftSubscription]);

    useEffect(() => {
        if (!isExistingSubscription && !isServiceSubscription && !isBroadbandSubscription && !isPrepaidSubscription) {
            dispatch(customerSwitchPhoneNumberPanel(CUSTOMER_TYPE.PORT_IN, subscriptionId));
        } else if (!isExistingSubscription && isPrepaidSubscription) {
            dispatch(customerSwitchPhoneNumberPanel(CUSTOMER_TYPE.NEW, subscriptionId));
        }
    }, [isExistingSubscription, isServiceSubscription, isBroadbandSubscription, isPrepaidSubscription, subscriptionId]);

    useEffect(() => {
        if (!showKeepCurrent && selectedSim === "keep_current") {
            setSelectedSim("esim");
        }
    }, [showKeepCurrent, selectedSim]);

    useEffect(() => {
        setSelectedSim((prevSelectedSim) => {
            if (hasPrefilledSimSelection) {
                return prevSelectedSim;
            }

            if (showKeepCurrent && prevSelectedSim !== "keep_current") {
                return "keep_current";
            }

            if (
                (selectedPanel === CUSTOMER_TYPE.PORT_IN || selectedPanel === CUSTOMER_TYPE.NEW) &&
                prevSelectedSim === "esim"
            ) {
                return "sim";
            }

            return prevSelectedSim;
        });
    }, [selectedPanel, showKeepCurrent, hasPrefilledSimSelection]);

    const handleFirstStepNextClick = () => {
        const isPortIn = selectedPanel === CUSTOMER_TYPE.PORT_IN;
        setShowPortinStep(isPortIn);
        setSelectedStep("simCard");
    };

    useEffect(() => {
        // Skip when a real number is already saved (re-editing a confirmed subscription); a fresh draft has none yet.
        if (
            (isServiceSubscription || isBroadbandSubscription) &&
            activeSubscription &&
            !existingSubscriptionPhoneNumber
        ) {
            dispatch(customerSwitchPhoneNumberPanel(CUSTOMER_TYPE.NEW, activeSubscription));
        }
    }, [isServiceSubscription, isBroadbandSubscription, activeSubscription, existingSubscriptionPhoneNumber]);

    useEffect(() => {
        if (!existingStoreSimNumber) {
            return;
        }

        // Prefill once when the value arrives, but don't overwrite user edits.
        setSimNumber((current) => current || existingStoreSimNumber.replace(/\D/g, ""));
    }, [existingStoreSimNumber]);

    const applySimSelectionToState = (): boolean => {
        const simNumberError =
            selectedSim === "sim" && selectedSimDelivery === "from_store"
                ? getSimNumberError(simNumber) || duplicateSimNumberError
                : undefined;

        // Validate EID for eSIM Push (applies to all subscriptions)
        if (selectedSim === "esim" && esimType === "esim-push") {
            if (!eid || eid.length !== 32 || (!eid.startsWith("89") && !eid.startsWith("35"))) {
                setShowSimErrors(true);
                return false;
            }
        }

        if (simNumberError) {
            setShowSimErrors(true);
            return false;
        }
        dispatch(setValue("isEsimSelected", selectedSim === "esim"));
        dispatch(setValue("isKeepCurrentSim", selectedSim === "keep_current"));
        dispatch(setValue("isPhysicalSimSelected", selectedSim === "sim"));
        dispatch(setValue("isSimFromStoreSelected", selectedSim === "sim" && selectedSimDelivery === "from_store"));
        if (selectedSim === "sim" && selectedSimDelivery === "from_store") {
            dispatch(setValue("storeSimNumber", simNumber));
        }
        // Set eSIM type and EID for all subscriptions when eSIM is selected
        if (selectedSim === "esim") {
            const esimTypeValue = esimType === "esim-qr" ? "ESIM-QR" : "ESIM-PUSH";
            dispatch(setValue("esimType", esimTypeValue));
            dispatch(setValue("eid", esimType === "esim-push" ? eid : ""));
        } else {
            dispatch(setValue("esimType", ""));
            dispatch(setValue("eid", ""));
        }
        return true;
    };

    const closeExistingInlineEdit = () => {
        if (isExistingSubscription && isDraftSubscription && subscriptionId) {
            setDiscardModalSubscriptionId(subscriptionId);
            return;
        }

        if (existingSubscriptionForChange?.phoneNumber) {
            dispatch(setReopenExistingSubscriptionPhone(existingSubscriptionForChange.phoneNumber));
        }
        dispatch(setActiveSubscription());
        if (existingSubscriptionForChange) {
            dispatch(clearExistingSubscriptionForChange());
            dispatch(setExistingSubscriptionForChange(undefined));
        }
    };

    const completeExistingInlineEdit = () => {
        if (existingSubscriptionForChange?.phoneNumber) {
            dispatch(setReopenExistingSubscriptionPhone(existingSubscriptionForChange.phoneNumber));
        }
        dispatch(resetCustomerInfo());
        dispatch(setActiveSubscription());
        if (existingSubscriptionForChange) {
            dispatch(clearExistingSubscriptionForChange());
            dispatch(setExistingSubscriptionForChange(undefined));
        }
    };

    const handleSimScopeSubmit = async () => {
        if (!applySimSelectionToState()) {
            return;
        }

        const body: ChangeSimCardRequestBody = {
            subscriptionCartItemId: subscriptionId,
            isEsimSelected: selectedSim === "esim",
            esimType: selectedSim === "esim" ? (esimType === "esim-qr" ? "ESIM-QR" : "ESIM-PUSH") : undefined,
            eid: selectedSim === "esim" && esimType === "esim-push" ? eid : undefined,
            isKeepCurrentSim: selectedSim === "keep_current" || undefined,
            isPhysicalSimSelected: selectedSim === "sim" || undefined,
            isSimFromStoreSelected: (selectedSim === "sim" && selectedSimDelivery === "from_store") || undefined,
            storeSimNumber: selectedSim === "sim" && selectedSimDelivery === "from_store" ? simNumber : undefined,
        };

        const responsePromise = Ajax_post<{
            cart: { content?: unknown[]; bookStartTime?: string; bookEndTime?: string };
        }>({ url: API_URL_RETAILER_CHANGE_SIM_CARD, data: body }, dispatch);

        dispatch({ type: RETAILER_SET_CUSTOMER_DETAILS, payload: responsePromise });

        try {
            await responsePromise;
            toast.success("SIM-kortet er endret", { position: "top-center" });
            completeExistingInlineEdit();
        } catch {
            // Ajax_post already handles error display via dispatch
        }
    };

    const handleExtraSimScopeSubmit = async ({
        simType,
        productCode,
        simConfig,
    }: {
        simType: ExtraSimType;
        productCode: string;
        simConfig: SimConfig;
    }) => {
        setIsSubmittingExtraSimChange(true);
        const isEsim = simConfig.simChoice === SimChoice.ESim;

        try {
            await dispatch(
                addExtraSimToCart(
                    simType,
                    subscriptionId,
                    isEsim,
                    existingSubscriptionForChange?.phoneNumber || "",
                    false,
                    productCode,
                    !isEsim && !!simConfig.simNumber,
                    !isEsim ? simConfig.simNumber : undefined,
                    "change",
                ),
            );
            toast.success("SIM-kortet er endret", { position: "top-center" });
            completeExistingInlineEdit();
        } catch {
            // Ajax_put already handles error display via dispatch
        } finally {
            setIsSubmittingExtraSimChange(false);
        }
    };

    // For broadband we skip the product user step and set productUser = legalOwner directly.
    const handleBroadbandSimStepSubmit = async () => {
        if (!applySimSelectionToState()) {
            return;
        }

        dispatch(setValue("isProductUserDifferentThanLegalOwner", false));
        setIsSubmittingBroadbandUser(true);

        try {
            const response = await dispatch(
                setCustomerDetails({ customerId: subscriptionId, isOptingOutForTeliaXServices: false }),
            );

            if (response.hasUpdatedCustomer) {
                toast.success("Abonnementet er oppdatert", { position: "top-center" });
                dispatch(resetCustomerInfo());
                // Mark done with the visible steps (SIM is the last one shown for broadband) rather than
                // jumping to the hidden product user step, which would briefly re-surface it.
                dispatch(setCompletedSubscriptionSteps(subscriptionId, 2));
                dispatch(setActiveSubscription());
                return;
            }
        } catch {
            // fall through and let the customer fill in the product user step manually
        } finally {
            setIsSubmittingBroadbandUser(false);
        }

        // Something prevented us from using the legal owner as product user, let the customer fill it in manually.
        dispatch(nextSubscriptionStep(subscriptionId));
    };

    const handleSimStepNextClickWithPortin = () => {
        if (!applySimSelectionToState()) {
            return;
        }
        if (showPortinStep) {
            setSelectedStep("portinDate");
            return;
        }
        dispatch(nextSubscriptionStep(subscriptionId));
    };

    return (
        <div>
            {!!breakageFeeAmount &&
                existingSubscriptionEditScope !== "sim" &&
                existingSubscriptionEditScope !== "productUser" && (
                    <Notification type="tile" variant="information" mb="200">
                        <Notification.Icon />
                        <Notification.Text>Eventuelt bruddgebyr vil bli {breakageFeeAmount},-</Notification.Text>
                    </Notification>
                )}
            {!isServiceSubscription && !isBroadbandSubscription && !isSimScopeChange && (
                <>
                    {!isExistingSubscription && (
                        <CollapsibleStep
                            label="Velg nummer"
                            onClick={() => setSelectedStep("phoneNumber")}
                            stepId="phoneNumber"
                            selectedStep={selectedStep}
                        >
                            <ChoosePhoneNumber
                                choosePhoneNumber={(phoneNumber) => {
                                    dispatch(resetCurrentPhoneNumberCheckMessages(subscriptionId));
                                    dispatch(chooseCustomerPhoneNumber(phoneNumber, subscriptionId));
                                }}
                                choosePhoneNumberSwitchPanel={(activePanel) => {
                                    dispatch(customerSwitchPhoneNumberPanel(activePanel, subscriptionId));
                                }}
                                selectedPhoneNumber={_get(userForm, "values.user.number")}
                                messages={currentPhoneNumberCheckMessages}
                                subscriptionId={subscriptionId}
                                showExistingOption={isExistingSubscription}
                                isAllowedToProceed={showAddCustomerInputFields}
                                onNextButtonClick={handleFirstStepNextClick}
                            />
                        </CollapsibleStep>
                    )}
                    <CollapsibleStep
                        label="Velg SIM-kort"
                        onClick={() => setSelectedStep("simCard")}
                        stepId="simCard"
                        selectedStep={selectedStep}
                        isDisabled={
                            !showAddCustomerInputFields ||
                            (selectedStep && stepOrder["simCard"] > stepOrder[selectedStep])
                        }
                    >
                        <ChooseSimCard
                            selectedSim={selectedSim}
                            setSelectedSim={setSelectedSim}
                            selectedSimDelivery={selectedSimDelivery}
                            setSelectedSimDelivery={setSelectedSimDelivery}
                            simNumber={simNumber}
                            setSimNumber={setSimNumber}
                            showErrors={showSimErrors}
                            externalSimNumberError={duplicateSimNumberError}
                            showKeepCurrent={showKeepCurrent}
                            isPrepaid={isPrepaidSubscription}
                            esimType={esimType}
                            setEsimType={setEsimType}
                            eid={eid}
                            setEid={setEid}
                            onSimNumberInUseChange={setIsSimNumberInUse}
                        >
                            <SubscriptionStepsControls
                                nextButtonId="sim-card-next-button"
                                onSecondaryClick={
                                    isExistingSubscription ? undefined : () => setSelectedStep("phoneNumber")
                                }
                                onPrimaryClick={handleSimStepNextClickWithPortin}
                                primaryDisabled={isSimNumberInUse}
                            />
                        </ChooseSimCard>
                    </CollapsibleStep>
                    {showPortinStep && (
                        <CollapsibleStep
                            label="Velg ønsket aktiveringsdato"
                            onClick={() => setSelectedStep("portinDate")}
                            stepId="portinDate"
                            selectedStep={selectedStep}
                            isDisabled={
                                !showAddCustomerInputFields ||
                                (selectedStep && stepOrder["portinDate"] > stepOrder[selectedStep])
                            }
                        >
                            <PortingDateCard
                                subscriptionId={subscriptionId}
                                onBackButtonClick={() => setSelectedStep("simCard")}
                                selectedSim={selectedSim}
                                selectedSimDelivery={selectedSimDelivery}
                                description="Datoen kan endres senere. Kunden vil motta en SMS med mer informasjon om denne muligheten."
                            />
                        </CollapsibleStep>
                    )}
                </>
            )}
            {!isServiceSubscription && isBroadbandSubscription && (
                <ChooseSimCard
                    selectedSim={selectedSim}
                    setSelectedSim={setSelectedSim}
                    selectedSimDelivery={selectedSimDelivery}
                    setSelectedSimDelivery={setSelectedSimDelivery}
                    simNumber={simNumber}
                    setSimNumber={setSimNumber}
                    showErrors={showSimErrors}
                    externalSimNumberError={duplicateSimNumberError}
                    showKeepCurrent={showKeepCurrent}
                    isPrepaid={isPrepaidSubscription}
                    esimType={esimType}
                    setEsimType={setEsimType}
                    eid={eid}
                    setEid={setEid}
                    onSimNumberInUseChange={setIsSimNumberInUse}
                >
                    <SubscriptionStepsControls
                        nextButtonId="sim-card-next-button"
                        onPrimaryClick={handleBroadbandSimStepSubmit}
                        primaryDisabled={isSimNumberInUse || isSubmittingBroadbandUser}
                        isProcessing={isSubmittingBroadbandUser}
                    />
                </ChooseSimCard>
            )}
            {isServiceSubscription && (
                <>
                    <Notification type="tile" variant="information" className="identification__notification" mb="200">
                        <Notification.Icon />
                        <Notification.Text>Trenger ikke å velge nummer for denne type abonnement.</Notification.Text>
                    </Notification>
                    <SubscriptionStepsControls />
                </>
            )}
            {isSimScopeChange && hasChangeableExtraSims && !hasChosenVoiceSimType && (
                <ChooseSimType
                    additionalSimcards={changeableAdditionalSimcards}
                    onSelectVoice={() => setHasChosenVoiceSimType(true)}
                    onConfirmExtraSim={handleExtraSimScopeSubmit}
                    isSubmitting={isSubmittingExtraSimChange}
                />
            )}
            {isSimScopeChange && (!hasChangeableExtraSims || hasChosenVoiceSimType) && (
                <ChooseSimCard
                    selectedSim={selectedSim}
                    setSelectedSim={setSelectedSim}
                    selectedSimDelivery={selectedSimDelivery}
                    setSelectedSimDelivery={setSelectedSimDelivery}
                    simNumber={simNumber}
                    setSimNumber={setSimNumber}
                    showErrors={showSimErrors}
                    externalSimNumberError={duplicateSimNumberError}
                    showKeepCurrent={showKeepCurrent}
                    isPrepaid={isPrepaidSubscription}
                    esimType={esimType}
                    setEsimType={setEsimType}
                    eid={eid}
                    setEid={setEid}
                    onSimNumberInUseChange={setIsSimNumberInUse}
                >
                    <SubscriptionStepsControls
                        nextButtonId="sim-card-next-button"
                        backLabel="Lukk"
                        nextLabel="Fullfør"
                        onSecondaryClick={closeExistingInlineEdit}
                        onPrimaryClick={handleSimScopeSubmit}
                        primaryDisabled={isLoading || isSimNumberInUse}
                        isProcessing={isLoading}
                    />
                </ChooseSimCard>
            )}
            <ExistingSubscriptionDiscardModal
                open={!!discardModalSubscriptionId}
                onClose={() => setDiscardModalSubscriptionId(null)}
                onConfirm={async () => {
                    if (!discardModalSubscriptionId) {
                        return;
                    }

                    if (existingSubscriptionForChange?.phoneNumber) {
                        dispatch(setReopenExistingSubscriptionPhone(existingSubscriptionForChange.phoneNumber));
                    }
                    dispatch(removeItemByIdFromCart(discardModalSubscriptionId, false));
                    dispatch(setActiveSubscription());
                    setDiscardModalSubscriptionId(null);
                    if (existingSubscriptionForChange) {
                        dispatch(clearExistingSubscriptionForChange());
                        dispatch(setExistingSubscriptionForChange(undefined));
                    }
                }}
            />
        </div>
    );
};

export default PhoneNumber;
